import { AddIcon } from '@chakra-ui/icons';
import { HStack } from '@chakra-ui/react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import useGetCountries from './queries/useGetCountries';
import useDeleteCountry from './queries/useDeleteCountry';

import { Button, ConfirmationDialog, Text } from '@/components/v4';
import AGGridMain from 'components/tables/AGGrid/AGGridMain';
import useColumnState from '../../../hooks/useColumnState';

import { getCountriesTableColumns } from './countriesTableConfig';

export default function Countries() {
    const gridRef = useRef();
    const navigate = useNavigate();

    const [tableData, setTableData] = useState({
        columns: [],
        data: [],
    });

    const [countryToDelete, setCountryToDelete] = useState(null);

    const handleEditCountry = useCallback(
        (countryData) => {
            navigate(`/countries/edit/${countryData.id}`);
        },
        [navigate],
    );

    const handleAddCountry = () => {
        navigate('/countries/add');
    };

    const handleDeleteCountry = useCallback((countryData) => {
        setCountryToDelete(countryData);
    }, []);

    const handleCloseDeleteDialog = () => {
        setCountryToDelete(null);
    };

    const handleDeleteSuccess = () => {
        toast.success('Country deleted successfully!');
        setCountryToDelete(null);
    };

    const handleDeleteError = () => {
        toast.error('Failed to delete country. Please try again.');
        setCountryToDelete(null);
    };

    const { mutate: deleteCountry, isPending: isDeleting } = useDeleteCountry(
        handleDeleteSuccess,
        handleDeleteError,
    );

    const handleConfirmDelete = () => {
        if (countryToDelete) {
            deleteCountry(countryToDelete.id);
        }
    };

    const baseColumns = useMemo(
        () =>
            getCountriesTableColumns({
                onEditCountry: handleEditCountry,
                onDeleteCountry: handleDeleteCountry,
            }),
        [handleEditCountry, handleDeleteCountry],
    );

    const { columns, onColumnVisible } = useColumnState(baseColumns);

    const tableDataWithColumns = useMemo(
        () => ({
            columns,
            data: tableData.data,
        }),
        [columns, tableData.data],
    );

    const { refetch, isRefetching, isFetching } = useGetCountries(
        setTableData,
        gridRef,
        baseColumns,
    );

    const isFetchingData = isRefetching || isFetching;

    const titleName = 'Countries';

    return (
        <>
            <HStack justifyContent="space-between" marginBottom={5}>
                <Text sx={{ fontSize: '20', fontWeight: '500' }}>
                    Countries
                </Text>
            </HStack>

            <AGGridMain
                gridRef={gridRef}
                tableData={tableDataWithColumns}
                isLoading={isFetchingData}
                titleName={titleName}
                refetch={refetch}
                onColumnVisible={onColumnVisible}
                extraComponents={
                    <HStack pl={5} spacing={2}>
                        <Button
                            leftIcon={<AddIcon />}
                            onClick={handleAddCountry}
                            size="sm"
                            padding="6px 12px">
                            Add Country
                        </Button>
                    </HStack>
                }
            />

            <ConfirmationDialog
                isOpen={!!countryToDelete}
                onClose={handleCloseDeleteDialog}
                onConfirm={handleConfirmDelete}
                title="Delete Country"
                message={`If you wish to delete "${countryToDelete?.name || 'this country'}", please ensure it is not in use by a number/address. After deleting, this country will no longer be serviced. Are you sure you want to continue?`}
                confirmText="Delete"
                confirmColorScheme="red"
                isLoading={isDeleting}
            />
        </>
    );
}
